import PanelHeader from '../../components/panelheader/panelHeader';
import Vurderingsinstillinger from './vurderingsinnstillinger';
import Vurderingskriterier from './vurderingskriterier';
import { useActivityContextUHA } from '../activitycontexts';
import { useLoader } from '../../api/useLoader';
import {
  LaeringsutbyttegruppeMinimalDto,
  VurderingsinnstillingerDto,
} from '../../api/generated/studiested-generated-api';
import React, { useContext, useMemo, useState } from 'react';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import * as css from './vurderingWrapper.module.scss';
import translations from './vurderingWrapper.i18n';
import { useIntl } from 'react-intl';
import Spinner from '../../components/spinner/spinner';
import { RadioButton, RadioGroup } from '../../components/input/radioButton';

type VurderingRadioFormType = {
  active: string;
  setActive: (active: string) => void;
  aktivitetId: string;
};

const VurderingRadioForm: React.FC<
  React.PropsWithChildren<VurderingRadioFormType>
> = ({ active, setActive, aktivitetId }) => {
  const { praksisaktivitetVurderingApi } = useContext(StudiestedApiContext);
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      useVurderingInfoText: formatMessage({
        id: translations.useVurderingInfoText.id,
      }),
      yes: formatMessage({ id: translations.yes.id }),
      no: formatMessage({ id: translations.no.id }),
    }),
    [formatMessage],
  );

  const onChosingNo = (no: string) => {
    setActive(no);
    praksisaktivitetVurderingApi.praksisaktivitetAktivitetIdVurderingInnstillingerPut(
      {
        pathParams: {
          aktivitetId,
        },
        vurderingsinnstillingerDto: {
          usesVurdering: false,
          roller: { underviser: false, veileder: false, student: false },
          vurderingstyper: {
            midtveisvurdering: false,
            kontinuerlig: false,
            sluttvurdering: false,
          },
        },
      },
    );
  };

  return (
    <RadioGroup label={labels.useVurderingInfoText} className={css.container}>
      <RadioButton
        id={'vurderingsinstillinger-yes'}
        value={labels.yes}
        label={labels.yes}
        checked={active === labels.yes}
        name={'vurderingsinstillinger'}
        handleChange={setActive}
      />
      <RadioButton
        id={'vurderingsinstillinger-no'}
        value={labels.no}
        label={labels.no}
        checked={active === labels.no}
        name={'vurderingsinstillinger'}
        handleChange={onChosingNo}
      />
    </RadioGroup>
  );
};

const VurderingWrapper = () => {
  const {
    praksisaktivitet: { benytterVurdering, id: aktivitetId },
  } = useActivityContextUHA();

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      yes: formatMessage({ id: translations.yes.id }),
      no: formatMessage({ id: translations.no.id }),
    }),
    [formatMessage],
  );

  const [vurderingActive, setVurderingActive] = useState<string>(
    benytterVurdering ? labels.yes : labels.no,
  );

  return (
    <>
      <VurderingRadioForm
        active={vurderingActive}
        setActive={setVurderingActive}
        aktivitetId={aktivitetId}
      />
      {vurderingActive === labels.yes && <VurderingDataWrapper />}
    </>
  );
};

const VurderingDataWrapper = () => {
  const { praksisaktivitetVurderingApi } = useContext(StudiestedApiContext);
  const {
    praksisaktivitet: { id: aktivitetId },
  } = useActivityContextUHA();

  const { data: vurderingskriterier, loading: kriterierLoading } = useLoader(
    () =>
      praksisaktivitetVurderingApi.praksisaktivitetAktivitetIdVurderingLaeringsutbyttegruppeGet(
        {
          pathParams: { aktivitetId },
        },
      ),
    [aktivitetId],
  );

  const { data: vurderingsinnstillinger, loading: innstillingerLoading } =
    useLoader(
      () =>
        praksisaktivitetVurderingApi.praksisaktivitetAktivitetIdVurderingInnstillingerGet(
          {
            pathParams: { aktivitetId: aktivitetId },
          },
        ),
      [aktivitetId],
    );

  const isLoading = useMemo(
    () => kriterierLoading || innstillingerLoading,
    [kriterierLoading, innstillingerLoading],
  );

  if (isLoading) return <Spinner />;

  if (!vurderingsinnstillinger || !vurderingskriterier) return <></>;

  return (
    <Vurdering
      vurderingskriterier={vurderingskriterier}
      vurderingsinnstillinger={vurderingsinnstillinger}
    />
  );
};

type VurderingType = {
  vurderingsinnstillinger: VurderingsinnstillingerDto;
  vurderingskriterier: LaeringsutbyttegruppeMinimalDto[];
};

const Vurdering: React.FC<React.PropsWithChildren<VurderingType>> = ({
  vurderingsinnstillinger,
  vurderingskriterier,
}) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      vurderingsinstillingerTitle: formatMessage({
        id: translations.vurderingsinstillingerTitle.id,
      }),
      vurderingskriterierTitle: formatMessage({
        id: translations.vurderingskriterierTitle.id,
      }),
      vurderingsinnstillingerSubtitle: formatMessage({
        id: translations.vurderingsinnstillingerSubtitle.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <>
      <PanelHeader
        title={labels.vurderingsinstillingerTitle}
        subtitle={labels.vurderingsinnstillingerSubtitle}
        collapsible
      >
        <Vurderingsinstillinger
          vurderingsinnstillinger={vurderingsinnstillinger}
        />
      </PanelHeader>
      <PanelHeader title={labels.vurderingskriterierTitle} collapsible>
        <Vurderingskriterier
          vurderingskriteriumsamlinger={vurderingskriterier}
        />
      </PanelHeader>
    </>
  );
};

export default VurderingWrapper;
